import { LEVEL_WIDTH, TileData, TileType } from "./types";
import { tileKey, type EnemySpawn, type LevelData } from "./level";

// World 4-3: Floating platforms, many gaps, tricky jumps
export function buildLevel4_3(): LevelData {
  const tiles = new Map<string, TileData>();
  const groundTop = -1;
  const groundDepth = 2;

  const pits: Array<[number, number]> = [
    [16, 18],
    [32, 34],
    [48, 50],
    [64, 66],
    [80, 82],
    [96, 98],
    [112, 114],
    [128, 130],
    [144, 146],
    [160, 162],
    [176, 178]
  ];

  const setTile = (x: number, y: number, type: TileType, payload?: TileData["payload"], flower?: boolean): void => {
    tiles.set(tileKey(x, y), { type, payload, broken: false, bump: 0, flower: flower || undefined });
  };

  const isPit = (x: number): boolean => pits.some(([a, b]) => x >= a && x <= b);

  for (let x = 0; x < LEVEL_WIDTH; x++) {
    if (isPit(x)) continue;
    for (let dy = 0; dy < groundDepth; dy++) {
      setTile(x, groundTop - dy, TileType.Ground);
    }
  }

  const stair = (x: number, h: number): void => {
    for (let dy = 0; dy < h; dy++) setTile(x, groundTop + 1 + dy, TileType.Block);
  };

  // floating platforms over each pit
  for (let x = 20; x <= 25; x++) setTile(x, 4, TileType.Brick);
  setTile(22, 4, TileType.Question, { kind: "mushroom" });
  setTile(24, 4, TileType.Question, { kind: "coin" });

  for (let x = 36; x <= 41; x++) setTile(x, 4, TileType.Brick);
  setTile(38, 4, TileType.Question, { kind: "coin" });
  setTile(40, 4, TileType.Question, { kind: "mushroom" });

  for (let x = 52; x <= 57; x++) setTile(x, 4, TileType.Brick);
  setTile(54, 4, TileType.Question, { kind: "coin" });

  for (let x = 68; x <= 73; x++) setTile(x, 4, TileType.Brick);
  setTile(70, 4, TileType.Question, { kind: "coin" });
  setTile(72, 4, TileType.Question, { kind: "mushroom" });

  for (let x = 84; x <= 89; x++) setTile(x, 4, TileType.Brick);
  setTile(86, 4, TileType.Question, { kind: "coin" });

  for (let x = 100; x <= 105; x++) setTile(x, 4, TileType.Brick);
  setTile(102, 4, TileType.Question, { kind: "mushroom" });
  setTile(104, 4, TileType.Question, { kind: "coin" });

  for (let x = 116; x <= 121; x++) setTile(x, 4, TileType.Brick);
  setTile(118, 4, TileType.Question, { kind: "coin" });

  for (let x = 132; x <= 137; x++) setTile(x, 4, TileType.Brick);
  setTile(134, 4, TileType.Question, { kind: "coin" });
  setTile(136, 4, TileType.Question, { kind: "mushroom" });

  for (let x = 148; x <= 153; x++) setTile(x, 4, TileType.Brick);
  setTile(150, 4, TileType.Question, { kind: "coin" });

  for (let x = 164; x <= 169; x++) setTile(x, 4, TileType.Brick);
  setTile(166, 4, TileType.Question, { kind: "mushroom" });
  setTile(168, 4, TileType.Question, { kind: "coin" });

  for (let x = 180; x <= 185; x++) setTile(x, 4, TileType.Brick);
  setTile(182, 4, TileType.Question, { kind: "coin" });
  setTile(184, 4, TileType.Question, { kind: "coin" });

  // pipes between floating platforms
  const pipes: Array<[number, number]> = [
    [28, 2],
    [44, 3],
    [60, 2],
    [76, 3],
    [92, 2],
    [108, 3],
    [124, 2],
    [140, 3],
    [156, 2],
    [172, 3]
  ];
  for (const [px, h] of pipes) {
    for (let dy = 0; dy < h; dy++) {
      setTile(px, groundTop + 1 + dy, TileType.Pipe);
      setTile(px + 1, groundTop + 1 + dy, TileType.Pipe);
    }
  }

  // high coins
  setTile(26, 7, TileType.Question, { kind: "coin" });
  setTile(42, 7, TileType.Question, { kind: "coin" });
  setTile(58, 7, TileType.Question, { kind: "coin" });
  setTile(74, 7, TileType.Question, { kind: "coin" });
  setTile(90, 7, TileType.Question, { kind: "coin" });
  setTile(106, 7, TileType.Question, { kind: "coin" });
  setTile(122, 7, TileType.Question, { kind: "coin" });
  setTile(138, 7, TileType.Question, { kind: "coin" });
  setTile(154, 7, TileType.Question, { kind: "coin" });
  setTile(170, 7, TileType.Question, { kind: "coin" });
  setTile(186, 7, TileType.Question, { kind: "coin" });

  stair(188, 1);
  stair(189, 2);
  stair(190, 3);
  stair(191, 4);
  stair(192, 4);
  stair(193, 4);
  stair(194, 3);
  stair(195, 2);
  stair(196, 1);

  const flagX = 198;
  for (let y = groundTop + 1; y <= 21; y++) setTile(flagX, y, TileType.Pole);
  setTile(flagX, 22, TileType.Block);

  const castleX = 202;

  const enemies: EnemySpawn[] = [
    { kind: "goomba", x: 21 },
    { kind: "koopa", x: 28 },
    { kind: "goomba", x: 37 },
    { kind: "goomba", x: 44 },
    { kind: "koopa", x: 53 },
    { kind: "goomba", x: 69 },
    { kind: "goomba", x: 76 },
    { kind: "koopa", x: 85 },
    { kind: "goomba", x: 101 },
    { kind: "goomba", x: 108 },
    { kind: "koopa", x: 117 },
    { kind: "goomba", x: 133 },
    { kind: "goomba", x: 140 },
    { kind: "koopa", x: 149 },
    { kind: "goomba", x: 165 },
    { kind: "goomba", x: 172 },
    { kind: "koopa", x: 181 },
    { kind: "goomba", x: 187 }
  ];

  return { tiles, enemies, enterablePipes: [], flagX, castleX, width: LEVEL_WIDTH, groundY: 0 };
}
